import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import subservicesData from "../data/servicesData";

const SubServicePage = () => {
  const { slug } = useParams();

  const service = subservicesData.find(
    (item) => item.slug.toLowerCase() === slug?.toLowerCase()
  );

  if (!service) return <Navigate to="/services" replace />;

  return (
    <>
      {/* ================= SEO ================= */}
      <Helmet>
        <title>{service.seoTitle}</title>
        <meta name="description" content={service.seoDescription} />
        <meta name="keywords" content={service.keywords} />

        {/* Open Graph */}
        <meta property="og:title" content={service.seoTitle} />
        <meta property="og:description" content={service.seoDescription} />
        <meta property="og:image" content={service.image} />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={service.seoTitle} />
        <meta name="twitter:description" content={service.seoDescription} />

        {/* Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.title,
            description: service.seoDescription,
            image: service.image,
            provider: {
              "@type": "LocalBusiness",
              name: "MA Perfect Fix",
              telephone: " +971 50 414 7991",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Dubai",
                addressCountry: "AE",
              },
            },
          })}
        </script>
      </Helmet>

      {/* ================= PAGE ================= */}
      <section className="bg-[#f9fafb] pt-28 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">

          {/* Luxury Card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] shadow-[0_40px_120px_rgba(0,0,0,0.08)] overflow-hidden">

            <div className="grid grid-cols-1 lg:grid-cols-2">

              {/* Image */}
              <div className="relative h-[260px] sm:h-[340px] md:h-[420px] lg:h-full">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-white/40 to-transparent" />
              </div>

              {/* Content */}
              <div className="p-6 sm:p-10 md:p-14 lg:p-16 flex flex-col justify-center">

                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-cormorant text-[#b8942e] tracking-wide mb-6">
                  {service.title}
                </h1>

                <p className="text-[#0a1f44] text-base sm:text-lg leading-relaxed mb-10">
                  {service.longDescription}
                </p>

                {/* Features */}
                <div className="mb-12">
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#b8942e] mb-6">
                    What We Offer
                  </h2>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {service.features.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-4 bg-white/70 border border-gray-100 rounded-xl px-5 py-4 shadow-sm hover:shadow-lg transition"
                      >
                        <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600" />
                        <span className="text-[#0a1f44]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <Link
                  to="/services"
                  state={{ scrollToForm: true, prefillService: service.title }}
                  className="inline-flex items-center justify-center w-fit px-10 py-4 rounded-full text-white text-base sm:text-lg font-semibold bg-gradient-to-r from-yellow-500 to-yellow-600 hover:scale-105 hover:shadow-xl transition"
                >
                  Request This Service
                </Link>

              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SubServicePage;
